import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Waves, Users, Plus, Star, StarOff, Eye, Image as ImageIcon, Video, X, ExternalLink,
  Trash2, Search, MessageCircle, Smile, SendHorizontal, ChevronDown, ChevronUp, Edit2,
  BarChart3, CheckCircle2, Download, FileText,
} from 'lucide-react';
import { Button, Avatar, Modal } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { streamsApi } from '../../services/api';
import { StreamThread, StreamCategory, Comment, MediaFile } from '../../types';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionText/MentionText';
import FormattedText from '../../utils/formatText';
import RichTextEditor from '../../components/RichTextEditor';
import styles from './Streams.module.css';

const reactionEmojis = ['👍', '👏', '❤️', '🎉', '💡', '🔥', '⭐', '😊'];

type TimePeriod = '7' | '30' | '90' | 'custom';

const Streams: React.FC = () => {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState<StreamThread[]>([]);
  const [categories, setCategories] = useState<StreamCategory[]>([]);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightWidenTimeRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [newThread, setNewThread] = useState({
    category_id: '',
    title: '',
    content: '',
    link_url: '',
    link_title: '',
  });
  const [createMediaFiles, setCreateMediaFiles] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileDocInputRef = useRef<HTMLInputElement>(null);
  // Poll editor state for the create modal — null when the author hasn't added one.
  const [newPoll, setNewPoll] = useState<{ question: string; options: string[] } | null>(null);
  const [pollVoting, setPollVoting] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Expanded card
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Filters
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });

  // Comments / reactions state
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentsData, setCommentsData] = useState<Record<string, Comment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState<Set<string>>(new Set());
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [submittingComment, setSubmittingComment] = useState<Set<string>>(new Set());
  const [deletingComment, setDeletingComment] = useState<Set<string>>(new Set());
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [hoverReactFor, setHoverReactFor] = useState<string | null>(null);
  const [hoverReactPos, setHoverReactPos] = useState<{ top: number; left: number } | null>(null);
  const hoverReactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [replyText, setReplyText] = useState('');
  const commentInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const canCreate = !!user;
  const canPin = user?.role === 'superadmin';
  const canEdit = (thread: StreamThread) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    if (thread.author_id === user.id) return true;
    return false;
  };
  const canDelete = canEdit;

  // Edit state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingThread, setEditingThread] = useState<StreamThread | null>(null);
  const [editFormData, setEditFormData] = useState({
    category_id: '',
    title: '',
    content: '',
    link_url: '',
    link_title: '',
  });
  const [editMediaFiles, setEditMediaFiles] = useState<MediaFile[]>([]);
  const [updating, setUpdating] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const editFileDocInputRef = useRef<HTMLInputElement>(null);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMedia, setLightboxMedia] = useState<MediaFile[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Viewers modal
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState<any[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersTitle, setViewersTitle] = useState('');
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [reactionsModalData, setReactionsModalData] = useState<any[]>([]);

  const canViewViewers = user && (user.role === 'superadmin' || user.role === 'admin');

  const handleViewViewers = useCallback(async (e: React.MouseEvent, thread: StreamThread) => {
    e.stopPropagation();
    setViewersTitle(thread.title);
    setShowViewersModal(true);
    setViewersLoading(true);
    try {
      const response = await streamsApi.getViewers(thread.id);
      setViewersData(response.data);
    } catch (err) {
      console.error('Failed to fetch viewers:', err);
      setViewersData([]);
    } finally {
      setViewersLoading(false);
    }
  }, [canViewViewers]);

  // Fetch categories
  useEffect(() => {
    streamsApi.getCategories()
      .then(res => setCategories(res.data))
      .catch(err => console.error('Failed to fetch categories:', err));
  }, []);

  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: 1 };
      if (timePeriod !== 'custom') {
        params.days = parseInt(timePeriod);
      }
      if (selectedCategoryId) {
        params.category_id = selectedCategoryId;
      }
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await streamsApi.getThreads(params);
      setThreads(response.data.threads || []);
    } catch (err) {
      console.error('Failed to fetch threads:', err);
    } finally {
      setLoading(false);
    }
  }, [timePeriod, selectedCategoryId, searchTerm]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  // Handle highlight from notification click; widen the time filter if the
  // item isn't in the current window
  useEffect(() => {
    const id = searchParams.get('highlight');
    if (!id || loading) return;
    if (threads.some(t => t.id === id)) {
      setHighlightId(id);
      setExpandedCard(id);
      // Open the comments section so a comment notification shows the comment
      setExpandedComments(prev => new Set(prev).add(id));
      if (!commentsData[id]) fetchComments(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-thread-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      setSearchParams({}, { replace: true });
      setTimeout(() => setHighlightId(null), 3000);
    } else if (timePeriod === '7' || timePeriod === '30') {
      highlightWidenTimeRef.current = Date.now();
      setTimePeriod('90');
    } else if (Date.now() - highlightWidenTimeRef.current > 1000) {
      // Widened window has finished loading and the item still isn't there — give up
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, loading, searchParams, setSearchParams, timePeriod]);

  // Client-side search filter (backend also does it; this catches stale state quickly)
  const filteredThreads = threads.filter(t => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      t.title?.toLowerCase().includes(s) ||
      t.content?.toLowerCase().includes(s) ||
      t.author_first_name?.toLowerCase().includes(s) ||
      t.author_last_name?.toLowerCase().includes(s)
    );
  });

  // Sidebar stats
  const overviewStats = useMemo(() => {
    const total = threads.length;
    const pinned = threads.filter(t => t.is_pinned).length;
    const unread = threads.filter(t => !t.is_read).length;
    return [
      { label: 'Total', value: total, color: '#0C6B62' },
      { label: 'Unread', value: unread, color: '#1D9E75' },
      { label: 'Pinned', value: pinned, color: '#D97706' },
    ];
  }, [threads]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    threads.forEach(t => {
      if (t.category_id) counts[t.category_id] = (counts[t.category_id] || 0) + 1;
    });
    return counts;
  }, [threads]);

  const recentActivity = useMemo(() => threads.slice(0, 3), [threads]);

  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setTimePeriod(period);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customDates.start && customDates.end) {
      setTimePeriod('custom');
      setShowCustomDatePicker(false);
      fetchThreads();
    }
  };

  const handleCreate = async () => {
    if (!newThread.category_id) {
      setError('Please choose a category');
      return;
    }
    if (!newThread.title.trim() || !newThread.content.trim()) {
      setError('Please fill in title and content');
      return;
    }
    // Validate poll inline so the user gets a clear message instead of a server 400
    if (newPoll) {
      const q = newPoll.question.trim();
      const opts = newPoll.options.map(o => o.trim()).filter(Boolean);
      if (!q) { setError('Poll question is required'); return; }
      if (opts.length < 2) { setError('Poll needs at least 2 non-empty options'); return; }
    }
    setCreating(true);
    setError('');
    try {
      const payload: any = {
        category_id: newThread.category_id,
        title: newThread.title,
        content: newThread.content,
      };
      if (createMediaFiles.length > 0) {
        payload.media_files = createMediaFiles;
      }
      if (newThread.link_url) {
        payload.link_url = newThread.link_url;
        payload.link_title = newThread.link_title || newThread.link_url;
      }
      if (newPoll) {
        payload.poll = {
          question: newPoll.question.trim(),
          options: newPoll.options.map(o => o.trim()).filter(Boolean),
        };
      }
      await streamsApi.createThread(payload);
      setShowCreateModal(false);
      setNewThread({ category_id: '', title: '', content: '', link_url: '', link_title: '' });
      setCreateMediaFiles([]);
      setNewPoll(null);
      fetchThreads();
    } catch (err: any) {
      console.error('Failed to create thread:', err);
      setError(err.response?.data?.error || 'Failed to create thread');
    } finally {
      setCreating(false);
    }
  };

  // Cast / change / withdraw vote — replace poll in local state with server response
  const handleVotePoll = async (threadId: string, optionId: string) => {
    if (pollVoting.has(threadId)) return;
    setPollVoting(prev => { const n = new Set(prev); n.add(threadId); return n; });
    try {
      const thread = threads.find(t => t.id === threadId);
      const alreadyOnThis = thread?.poll?.user_voted_option_id === optionId;
      const res = alreadyOnThis
        ? await streamsApi.unvotePoll(threadId)
        : await streamsApi.votePoll(threadId, optionId);
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, poll: res.data } : t));
    } catch (err) {
      console.error('Vote failed', err);
    } finally {
      setPollVoting(prev => { const n = new Set(prev); n.delete(threadId); return n; });
    }
  };

  const handleExportPoll = async (e: React.MouseEvent, threadId: string, question: string) => {
    e.stopPropagation();
    try {
      const res = await streamsApi.exportPollResults(threadId);
      const data = res.data;
      const rows: string[][] = [];
      rows.push(['Poll Question:', data.question]);
      rows.push(['Total Votes:', String(data.total_votes)]);
      rows.push([]);
      rows.push(['Option', 'Votes', 'Percentage']);
      data.summary.forEach((s: any) => {
        const pct = data.total_votes > 0 ? Math.round((s.vote_count / data.total_votes) * 100) : 0;
        rows.push([s.option_text, String(s.vote_count), `${pct}%`]);
      });
      rows.push([]);
      rows.push(['Name', 'Email', 'Role', 'Voted For', 'Voted At']);
      data.votes.forEach((v: any) => {
        rows.push([
          `${v.first_name} ${v.last_name}`,
          v.email,
          v.role,
          v.option_text,
          new Date(v.voted_at).toLocaleString('en-IN'),
        ]);
      });

      const csv = rows.map(r => r.map(c => `"${(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const bom = '﻿';
      const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poll-results-${question.slice(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export poll:', err);
    }
  };

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remaining = 5 - createMediaFiles.length;
    if (remaining <= 0) { setError('Maximum 5 files allowed'); return; }
    const filesToProcess = Array.from(files).slice(0, remaining);
    let errorMsg = '';
    filesToProcess.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { errorMsg = 'Each file must be less than 5MB'; return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      const reader = new FileReader();
      reader.onloadend = () => {
        setCreateMediaFiles(prev => prev.length >= 5 ? prev : [...prev, { url: reader.result as string, type: fileType as any, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (errorMsg) setError(errorMsg); else setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveMedia = (index: number) => {
    setCreateMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateString);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await streamsApi.deleteThread(deleteId);
      setThreads(prev => prev.filter(t => t.id !== deleteId));
      setDeleteModalOpen(false);
      setDeleteId(null);
    } catch (err) {
      console.error('Failed to delete thread:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleTogglePin = async (e: React.MouseEvent, thread: StreamThread) => {
    e.stopPropagation();
    try {
      const newPinned = !thread.is_pinned;
      await streamsApi.updateThread(thread.id, { is_pinned: newPinned });
      setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, is_pinned: newPinned } : t));
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    }
  };

  const handleEditClick = (e: React.MouseEvent, thread: StreamThread) => {
    e.stopPropagation();
    setEditingThread(thread);
    setEditFormData({
      category_id: thread.category_id || '',
      title: thread.title || '',
      content: thread.content || '',
      link_url: thread.link_url || '',
      link_title: thread.link_title || '',
    });
    const existingFiles: MediaFile[] = [];
    if (thread.media_files && thread.media_files.length > 0) {
      existingFiles.push(...thread.media_files);
    }
    setEditMediaFiles(existingFiles);
    setShowEditModal(true);
    setError('');
  };

  const handleEditMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const remaining = 5 - editMediaFiles.length;
    if (remaining <= 0) { setError('Maximum 5 files allowed'); return; }
    const filesToProcess = Array.from(files).slice(0, remaining);
    let errorMsg = '';
    filesToProcess.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { errorMsg = 'Each file must be less than 5MB'; return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditMediaFiles(prev => prev.length >= 5 ? prev : [...prev, { url: reader.result as string, type: fileType as any, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (errorMsg) setError(errorMsg); else setError('');
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleEditRemoveMedia = (index: number) => {
    setEditMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdate = async () => {
    if (!editingThread) return;
    if (!editFormData.category_id) {
      setError('Please choose a category');
      return;
    }
    if (!editFormData.title.trim() || !editFormData.content.trim()) {
      setError('Please fill in title and content');
      return;
    }
    setUpdating(true);
    setError('');
    try {
      const payload: any = {
        category_id: editFormData.category_id,
        title: editFormData.title,
        content: editFormData.content,
        media_files: editMediaFiles,
        link_url: editFormData.link_url || null,
        link_title: editFormData.link_url ? (editFormData.link_title || editFormData.link_url) : null,
      };
      await streamsApi.updateThread(editingThread.id, payload);
      setShowEditModal(false);
      setEditingThread(null);
      fetchThreads();
    } catch (err: any) {
      console.error('Failed to update thread:', err);
      setError(err.response?.data?.error || 'Failed to update thread');
    } finally {
      setUpdating(false);
    }
  };

  // Comments
  const fetchComments = async (threadId: string) => {
    setCommentsLoading(prev => new Set(prev).add(threadId));
    try {
      const response = await streamsApi.getComments(threadId);
      setCommentsData(prev => ({ ...prev, [threadId]: response.data }));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setCommentsLoading(prev => {
        const next = new Set(prev);
        next.delete(threadId);
        return next;
      });
    }
  };

  const toggleComments = async (e: React.MouseEvent, threadId: string) => {
    e.stopPropagation();
    const isExpanded = expandedComments.has(threadId);
    if (isExpanded) {
      setExpandedComments(prev => {
        const next = new Set(prev);
        next.delete(threadId);
        return next;
      });
    } else {
      setExpandedComments(prev => new Set(prev).add(threadId));
      if (!commentsData[threadId]) {
        await fetchComments(threadId);
      }
      setTimeout(() => {
        commentInputRefs.current[threadId]?.focus();
      }, 100);
    }
  };

  const handleSubmitComment = async (threadId: string) => {
    const content = newComment[threadId]?.trim();
    if (!content) return;
    setSubmittingComment(prev => new Set(prev).add(threadId));
    try {
      const response = await streamsApi.addComment(threadId, content);
      setCommentsData(prev => ({
        ...prev,
        [threadId]: [...(prev[threadId] || []), response.data]
      }));
      setThreads(prev => prev.map(t =>
        t.id === threadId ? { ...t, comment_count: (t.comment_count || 0) + 1 } : t
      ));
      setNewComment(prev => ({ ...prev, [threadId]: '' }));
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmittingComment(prev => {
        const next = new Set(prev);
        next.delete(threadId);
        return next;
      });
    }
  };

  const handleDeleteComment = async (threadId: string, commentId: string) => {
    setDeletingComment(prev => new Set(prev).add(commentId));
    try {
      await streamsApi.deleteComment(threadId, commentId);
      setCommentsData(prev => ({
        ...prev,
        [threadId]: prev[threadId]?.filter(c => c.id !== commentId) || []
      }));
      setThreads(prev => prev.map(t =>
        t.id === threadId ? { ...t, comment_count: Math.max(0, (t.comment_count || 1) - 1) } : t
      ));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeletingComment(prev => {
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.content);
  };

  const handleSaveEditComment = async (threadId: string, commentId: string) => {
    if (!editCommentText.trim()) return;
    setSavingComment(true);
    try {
      const response = await streamsApi.editComment(threadId, commentId, editCommentText.trim());
      setCommentsData(prev => ({
        ...prev,
        [threadId]: prev[threadId]?.map(c =>
          c.id === commentId ? { ...c, content: response.data.content } : c
        ) || []
      }));
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (err) {
      console.error('Failed to edit comment:', err);
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentReaction = async (threadId: string, commentId: string, emoji: string) => {
    try {
      await streamsApi.addCommentReaction(threadId, commentId, emoji);
      const response = await streamsApi.getComments(threadId);
      setCommentsData(prev => ({ ...prev, [threadId]: response.data }));
    } catch (err) { console.error(err); }
  };

  const handleReply = async (threadId: string, parentId: string) => {
    if (!replyText.trim()) return;
    try {
      const response = await streamsApi.addComment(threadId, replyText.trim(), parentId);
      setCommentsData(prev => ({ ...prev, [threadId]: [...(prev[threadId] || []), response.data] }));
      setThreads(prev => prev.map(t => t.id === threadId ? { ...t, comment_count: (t.comment_count || 0) + 1 } : t));
      setReplyingToId(null);
      setReplyText('');
    } catch (err) { console.error(err); }
  };

  const handleReaction = async (e: React.MouseEvent, threadId: string, emoji: string) => {
    e.stopPropagation();
    try {
      const response = await streamsApi.addReaction(threadId, emoji);
      const { action, previousEmoji } = response.data;
      setThreads(prev =>
        prev.map(t => {
          if (t.id !== threadId) return t;
          let reactions = [...(t.reactions || [])];
          if (action === 'removed') {
            reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
          } else if (action === 'replaced') {
            reactions = reactions.map(r => r.emoji === previousEmoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          } else {
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          }
          return { ...t, reactions };
        })
      );
    } catch (err) {
      console.error('Failed to add reaction:', err);
    }
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleCardClick = (thread: StreamThread) => {
    const isExpanded = expandedCard === thread.id;
    setExpandedCard(isExpanded ? null : thread.id);
    if (isExpanded) {
      setExpandedComments(prev => { const next = new Set(prev); next.delete(thread.id); return next; });
    }
    // Mark as viewed on expand
    if (!isExpanded && !thread.is_read) {
      streamsApi.getThreadById(thread.id).then(() => {
        setThreads(prev =>
          prev.map(t => t.id === thread.id ? { ...t, is_read: true, view_count: t.view_count + 1 } : t)
        );
      }).catch(() => {});
    }
  };

  const getMediaFiles = (thread: StreamThread): MediaFile[] => {
    if (thread.media_files && thread.media_files.length > 0) {
      return thread.media_files;
    }
    return [];
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1>Streams</h1>
          <p>Open threads across Matasma — culture, learning, questions.</p>
        </div>
        {canCreate && (
          <button className={styles.createBtn} onClick={() => setShowCreateModal(true)}>
            <Waves size={14} />
            Start a stream
          </button>
        )}
      </div>

      {/* Category Filter Row */}
      <div className={styles.categoryFilterRow}>
        <button
          className={`${styles.categoryChip} ${selectedCategoryId === null ? styles.active : ''}`}
          onClick={() => setSelectedCategoryId(null)}
        >
          All
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            className={`${styles.categoryChip} ${selectedCategoryId === cat.id ? styles.active : ''}`}
            onClick={() => setSelectedCategoryId(cat.id)}
          >
            <span>{cat.icon}</span>
            <span>{cat.name}</span>
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div className={styles.filtersRow}>
        <div className={styles.timeFilterGroup}>
          <button className={`${styles.timeFilterBtn} ${timePeriod === '7' ? styles.active : ''}`} onClick={() => handleTimePeriodChange('7')}>7 Days</button>
          <button className={`${styles.timeFilterBtn} ${timePeriod === '30' ? styles.active : ''}`} onClick={() => handleTimePeriodChange('30')}>30 Days</button>
          <button className={`${styles.timeFilterBtn} ${timePeriod === '90' ? styles.active : ''}`} onClick={() => handleTimePeriodChange('90')}>90 Days</button>
          <button className={`${styles.timeFilterBtn} ${timePeriod === 'custom' || showCustomDatePicker ? styles.active : ''}`} onClick={() => handleTimePeriodChange('custom')}>Custom</button>
        </div>

        <div className={styles.searchWrapper}>
          <Search size={16} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search streams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          {searchTerm && <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>×</button>}
        </div>
      </div>

      {showCustomDatePicker && (
        <div className={styles.customDatePicker}>
          <input type="date" value={customDates.start} onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })} className={styles.dateInput} />
          <span>to</span>
          <input type="date" value={customDates.end} onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })} className={styles.dateInput} />
          <Button size="sm" onClick={handleCustomDateApply}>Apply</Button>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>Loading threads...</span>
        </div>
      ) : filteredThreads.length === 0 ? (
        <div className={styles.empty}>
          <Users size={48} />
          <h3>No streams found</h3>
          <p>Try adjusting your filters, or start a new thread</p>
        </div>
      ) : (
        <div className={styles.mainGrid}>
          {/* Cards Column */}
          <div className={styles.cardList}>
            {filteredThreads.map(thread => {
              const isExpanded = expandedCard === thread.id;
              return (
                <div
                  key={thread.id}
                  data-thread-id={thread.id}
                  className={`${styles.threadCard} ${highlightId === thread.id ? styles.highlighted : ''}`}
                  onClick={() => handleCardClick(thread)}
                >
                  {/* Thread content */}
                  <div className={styles.threadInner}>
                    <div className={styles.threadAvatarCol}>
                      <Avatar
                        src={thread.author_avatar}
                        name={`${thread.author_first_name} ${thread.author_last_name}`}
                        size="sm"
                      />
                      <div className={styles.threadLine} />
                    </div>
                    <div className={styles.threadContentCol}>
                      <div className={styles.threadMetaRow}>
                        <span className={styles.threadHeroName}>{thread.author_first_name} {thread.author_last_name}</span>
                        {thread.category_name && (
                          <span className={styles.categoryPill}>{thread.category_icon} {thread.category_name}</span>
                        )}
                        {thread.is_pinned && <span className={styles.pinnedBadge}><Star size={11} /> Pinned</span>}
                        {!thread.is_read && <span className={styles.newBadge}>New</span>}
                        <span className={styles.threadMetaTime}>{formatTime(thread.created_at)}</span>
                      </div>
                      {/* Poll — shown before title/content */}
                      {thread.poll && (
                        <div className={styles.pollDisplay} onClick={(e) => e.stopPropagation()}>
                          <div className={styles.pollDisplayHeader}>
                            <BarChart3 size={14} />
                            <span className={styles.pollDisplayQuestion}>{thread.poll.question}</span>
                          </div>
                          <div className={styles.pollOptions}>
                            {thread.poll.options.map(opt => {
                              const userVoted = thread.poll!.user_voted_option_id === opt.id;
                              const total = thread.poll!.total_votes || 0;
                              const pct = total > 0 ? Math.round((opt.vote_count / total) * 100) : 0;
                              const busy = pollVoting.has(thread.id);
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  className={`${styles.pollOption} ${userVoted ? styles.pollOptionMine : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleVotePoll(thread.id, opt.id); }}
                                  disabled={busy}
                                >
                                  <span className={styles.pollOptionBar} style={{ width: `${pct}%` }} />
                                  <span className={styles.pollOptionContent}>
                                    <span className={styles.pollOptionLabel}>
                                      {userVoted && <CheckCircle2 size={13} />}
                                      {opt.option_text}
                                    </span>
                                    <span className={styles.pollOptionStats}>
                                      {opt.vote_count} · {pct}%
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          <div className={styles.pollFooter}>
                            <span>
                              {thread.poll.total_votes} vote{thread.poll.total_votes === 1 ? '' : 's'}
                              {thread.poll.user_voted_option_id && ' · Click your choice again to withdraw'}
                            </span>
                            {(thread.author_id === user?.id || user?.role === 'admin' || user?.role === 'superadmin') && thread.poll.total_votes > 0 && (
                              <button
                                className={styles.pollExportBtn}
                                onClick={(e) => handleExportPoll(e, thread.id, thread.poll!.question)}
                                title="Download poll results"
                              >
                                <Download size={13} /> Export
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      <h3 className={styles.threadTitle}>{thread.title}</h3>
                      {thread.link_url && (
                        <a href={thread.link_url} target="_blank" rel="noopener noreferrer" className={styles.linkPill} onClick={(e) => e.stopPropagation()}>
                          <ExternalLink size={13} /> <span>{thread.link_title || 'Link'}</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Body */}
                  <div className={styles.threadBody}>
                    {isExpanded ? (
                      <>
                        {/* Expanded: full-width text */}
                        <p className={`${styles.threadPreview} ${styles.threadPreviewExpanded}`}>
                          <FormattedText text={thread.content} />
                        </p>
                        {/* Media grid below text, full width */}
                        {(() => {
                          const files = getMediaFiles(thread);
                          if (files.length === 0) return null;
                          const mediaOnly = files.filter(f => f.type === 'image' || f.type === 'video');
                          const docOnly = files.filter(f => f.type !== 'image' && f.type !== 'video');
                          return (
                            <>
                              {mediaOnly.length > 0 && (
                                <div className={`${styles.mediaGrid} ${mediaOnly.length === 1 ? styles.mediaGrid1 : styles.mediaGrid2}`}>
                                  {mediaOnly.map((mf, i) => (
                                    <div key={i} className={styles.mediaGridItem} style={{ cursor: 'pointer' }}
                                      onClick={(e) => { e.stopPropagation(); setLightboxMedia(mediaOnly); setLightboxIndex(i); setLightboxOpen(true); }}>
                                      {mf.type === 'image' ? <img src={mf.url} alt="" /> : <video src={mf.url} controls onClick={(e) => e.stopPropagation()} />}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    ) : (
                      <div className={styles.threadBodyContent}>
                        <div className={styles.threadBodyText}>
                          <p className={`${styles.threadPreview} ${styles.threadPreviewClamped}`}>
                            <FormattedText text={thread.content} />
                          </p>
                        </div>
                        {(() => {
                          const files = getMediaFiles(thread);
                          const firstImage = files.find(f => f.type === 'image');
                          return firstImage ? (
                            <div
                              className={styles.threadThumb}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightboxMedia(files);
                                setLightboxIndex(0);
                                setLightboxOpen(true);
                              }}
                            >
                              <img src={firstImage.url} alt="" className={styles.threadThumbImage} />
                              {files.length > 1 && <span className={styles.threadThumbCount}>+{files.length - 1}</span>}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Reactions summary — clickable to open modal */}
                  {thread.reactions && thread.reactions.length > 0 && (
                    <div className={styles.threadEngagement} style={{ cursor: 'pointer' }} onClick={(e) => {
                      e.stopPropagation();
                      const allUsers = thread.reactions?.flatMap((r: any) =>
                        (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))
                      ) || [];
                      setReactionsModalData(allUsers);
                      setShowReactionsModal(true);
                    }}>
                      <div className={styles.reactionSummary}>
                        {thread.reactions.map(reaction => (
                          <span key={reaction.emoji} className={styles.reactionChip}>
                            {reaction.emoji} {reaction.count}
                            {reaction.users && reaction.users.length > 0 && (
                              <div className={styles.reactionTooltip}>
                                {reaction.users.slice(0, 10).map((u, i) => (
                                  <div key={i} className={styles.reactionUser}>{u.first_name} {u.last_name}</div>
                                ))}
                                {reaction.users.length > 10 && (
                                  <div className={styles.reactionUser}>+{reaction.users.length - 10} more</div>
                                )}
                              </div>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className={styles.threadFooter} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.threadFooterLeft}>
                      <span
                        className={`${styles.threadFooterStat} ${styles.threadFooterStatClickable}`}
                        onClick={(e) => handleViewViewers(e, thread)}
                        title="Click to see who viewed"
                      >
                        <Eye size={14} /> {thread.view_count} views
                      </span>
                      {(thread.reactions?.reduce((sum, r) => sum + r.count, 0) || 0) > 0 && (
                        <span
                          className={`${styles.threadFooterStat} ${styles.threadFooterStatClickable}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const allUsers = thread.reactions?.flatMap((r: any) =>
                              (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))
                            ) || [];
                            setReactionsModalData(allUsers);
                            setShowReactionsModal(true);
                          }}
                          title="Click to see who reacted"
                        >
                          <Smile size={14} /> {thread.reactions?.reduce((sum, r) => sum + r.count, 0)} reactions
                        </span>
                      )}
                    </div>
                    <div className={styles.threadFooterRight}>
                      <div className={styles.likeActionWrapper}>
                        <button className={styles.threadActionBtn} onClick={(e) => { e.stopPropagation(); }}>
                          <Smile size={15} />
                          <span>React</span>
                        </button>
                        <div className={styles.likeActionDropdown}>
                          {reactionEmojis.map(emoji => (
                            <button key={emoji} className={styles.emojiOption} onClick={(e) => handleReaction(e, thread.id, emoji)}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button className={styles.threadActionBtn} onClick={(e) => toggleComments(e, thread.id)}>
                        <MessageCircle size={15} />
                        <span>Comment{(thread.comment_count || 0) > 0 ? ` (${thread.comment_count})` : ''}</span>
                      </button>
                      {getMediaFiles(thread).filter(f => f.type !== 'image' && f.type !== 'video').map((mf, i) => (
                        <a key={i} href={mf.url} download={mf.name || 'file'} className={styles.threadActionBtn} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
                          <FileText size={15} />
                          <span>Download file</span>
                        </a>
                      ))}
                      {(canEdit(thread) || canPin || canDelete(thread)) && (
                        <span className={styles.threadFooterDivider} />
                      )}
                      {canEdit(thread) && (
                        <button
                          className={`${styles.threadIconBtn} ${styles.threadIconBtnEdit}`}
                          onClick={(e) => handleEditClick(e, thread)}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                      )}
                      {canPin && (
                        <button
                          className={`${styles.threadIconBtn} ${styles.threadIconBtnPin} ${thread.is_pinned ? styles.threadIconBtnPinActive : ''}`}
                          onClick={(e) => handleTogglePin(e, thread)}
                          title={thread.is_pinned ? 'Unpin' : 'Pin'}
                        >
                          {thread.is_pinned ? <StarOff size={14} /> : <Star size={14} />}
                        </button>
                      )}
                      {canDelete(thread) && (
                        <button
                          className={`${styles.threadIconBtn} ${styles.threadIconBtnDanger}`}
                          onClick={(e) => handleDeleteClick(e, thread.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Comments */}
                  {expandedComments.has(thread.id) && (
                    <div className={styles.commentsSection} onClick={(e) => e.stopPropagation()}>
                      {commentsLoading.has(thread.id) ? (
                        <div className={styles.commentsLoading}>
                          <div className={styles.smallSpinner}></div>
                          <span>Loading comments...</span>
                        </div>
                      ) : (
                        <>
                          {(commentsData[thread.id] || []).length > 0 && (() => {
                            const allComments = commentsData[thread.id] || [];
                            const topLevel = allComments.filter(c => !c.parent_id);
                            const getReplies = (parentId: string) => allComments.filter(c => c.parent_id === parentId);

                            const renderComment = (comment: Comment, isReply = false) => (
                              <div key={comment.id} className={styles.commentItem}>
                                <Avatar
                                  src={comment.avatar_url}
                                  name={`${comment.first_name} ${comment.last_name}`}
                                  size="sm"
                                />
                                <div className={styles.commentContent}>
                                  <div className={styles.commentHeader}>
                                    <span className={styles.commentAuthor}>{comment.first_name} {comment.last_name}</span>
                                    <span className={styles.commentTime}>{formatCommentDate(comment.created_at)}</span>
                                    {(user?.role === 'superadmin' || user?.id === comment.user_id) && editingCommentId !== comment.id && (
                                      <button
                                        className={styles.editCommentBtn}
                                        onClick={() => handleEditComment(comment)}
                                        title="Edit comment"
                                      >
                                        <Edit2 size={12} />
                                      </button>
                                    )}
                                    {(user?.role === 'superadmin' || user?.id === comment.user_id) && (
                                      <button
                                        className={styles.deleteCommentBtn}
                                        onClick={() => handleDeleteComment(thread.id, comment.id)}
                                        disabled={deletingComment.has(comment.id)}
                                        title="Delete comment"
                                      >
                                        {deletingComment.has(comment.id) ? (
                                          <div className={styles.tinySpinner}></div>
                                        ) : (
                                          <Trash2 size={12} />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                  {editingCommentId === comment.id ? (
                                    <div className={styles.editCommentRow}>
                                      <MentionInput
                                        mode="input"
                                        value={editCommentText}
                                        onChange={setEditCommentText}
                                        onSubmit={() => handleSaveEditComment(thread.id, comment.id)}
                                        placeholder="Edit comment..."
                                        disabled={savingComment}
                                        className={styles.mentionInputWrap}
                                      />
                                      <button className={styles.saveEditBtn} onClick={() => handleSaveEditComment(thread.id, comment.id)} disabled={savingComment || !editCommentText.trim()}>
                                        {savingComment ? <div className={styles.tinySpinner}></div> : <SendHorizontal size={14} />}
                                      </button>
                                      <button className={styles.cancelEditBtn} onClick={() => setEditingCommentId(null)}>
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <p className={styles.commentText}><MentionText text={comment.content} /></p>
                                  )}
                                  {comment.reactions && comment.reactions.length > 0 && (
                                    <div className={styles.commentReactions}>
                                      {comment.reactions.map(r => (
                                        <span
                                          key={r.emoji}
                                          className={styles.commentReactionChip}
                                          onClick={() => handleCommentReaction(thread.id, comment.id, r.emoji)}
                                        >
                                          {r.emoji} {r.count}
                                          {r.users && r.users.length > 0 && (
                                            <div className={styles.commentReactionTooltip}>
                                              {r.users.slice(0, 10).map((u, i) => (
                                                <div key={i}>{u.first_name} {u.last_name}</div>
                                              ))}
                                              {r.users.length > 10 && <div>+{r.users.length - 10} more</div>}
                                            </div>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {editingCommentId !== comment.id && (
                                    <div className={styles.commentActionRow}>
                                      <div
                                        className={styles.commentEmojiPicker}
                                        onMouseEnter={(e) => {
                                          if (hoverReactTimer.current) clearTimeout(hoverReactTimer.current);
                                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                          setHoverReactPos({ top: rect.top >= 50 ? rect.top - 42 : rect.bottom + 6, left: Math.max(8, Math.min(rect.left, window.innerWidth - 340)) });
                                          setHoverReactFor(comment.id);
                                        }}
                                        onMouseLeave={() => {
                                          hoverReactTimer.current = setTimeout(() => setHoverReactFor(null), 250);
                                        }}
                                      >
                                        <button className={styles.commentActionBtn}>
                                          <Smile size={12} /> React
                                        </button>
                                        {hoverReactFor === comment.id && hoverReactPos && (
                                          <div
                                            className={styles.commentEmojiDropdown}
                                            style={{ display: 'flex', position: 'fixed', top: hoverReactPos.top, left: hoverReactPos.left, bottom: 'auto' }}
                                          >
                                            {reactionEmojis.map((emoji) => (
                                              <button key={emoji} onClick={() => { handleCommentReaction(thread.id, comment.id, emoji); setHoverReactFor(null); }}>
                                                {emoji}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {!isReply && (
                                        <button className={styles.commentActionBtn} onClick={() => { setReplyingToId(replyingToId === comment.id ? null : comment.id); setReplyText(''); }}>
                                          <MessageCircle size={12} /> Reply
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );

                            return (
                              <div className={styles.commentsList}>
                                {topLevel.map(comment => (
                                  <div key={comment.id}>
                                    {renderComment(comment)}
                                    {getReplies(comment.id).length > 0 && (
                                      <div className={styles.replyGroup}>
                                        {getReplies(comment.id).map(reply => renderComment(reply, true))}
                                      </div>
                                    )}
                                    {replyingToId === comment.id && (
                                      <div className={styles.replyInputRow}>
                                        <div className={styles.commentInputContainer}>
                                          <MentionInput
                                          mode="input"
                                          placeholder={`Reply to ${comment.first_name}...`}
                                          value={replyText}
                                          onChange={setReplyText}
                                          onSubmit={() => handleReply(thread.id, comment.id)}
                                          className={styles.mentionInputWrap}
                                        />
                                          <button className={styles.sendCommentBtn} onClick={() => handleReply(thread.id, comment.id)} disabled={!replyText.trim()}>
                                          <SendHorizontal size={14} />
                                        </button>
                                        </div>
                                        <button className={styles.cancelEditBtn} onClick={() => { setReplyingToId(null); setReplyText(''); }}>
                                          <X size={14} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}

                          {(commentsData[thread.id] || []).length === 0 && (
                            <div className={styles.noComments}>
                              <MessageCircle size={20} />
                              <span>No comments yet. Be the first to comment!</span>
                            </div>
                          )}

                          <div className={styles.commentInputWrapper}>
                            <Avatar
                              src={user?.avatar_url}
                              name={`${user?.first_name} ${user?.last_name}`}
                              size="sm"
                            />
                            <div className={styles.commentInputContainer}>
                              <MentionInput
                                mode="input"
                                placeholder="Write a comment... Use @ to mention"
                                value={newComment[thread.id] || ''}
                                onChange={(val) => setNewComment(prev => ({ ...prev, [thread.id]: val }))}
                                onSubmit={() => handleSubmitComment(thread.id)}
                                disabled={submittingComment.has(thread.id)}
                                className={styles.mentionInputWrap}
                              />
                              <button
                                className={styles.sendCommentBtn}
                                onClick={() => handleSubmitComment(thread.id)}
                                disabled={!newComment[thread.id]?.trim() || submittingComment.has(thread.id)}
                              >
                                {submittingComment.has(thread.id) ? (
                                  <div className={styles.tinySpinner}></div>
                                ) : (
                                  <SendHorizontal size={16} />
                                )}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sidebar */}
          <div className={styles.sidebar}>
            {/* Overview */}
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>Overview</h3>
              <div className={styles.overviewList}>
                {overviewStats.map((s, i) => (
                  <div key={i} className={styles.overviewItem}>
                    <span className={styles.overviewLabel}>{s.label}</span>
                    <span className={styles.overviewValue} style={{ color: s.color }}>{s.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories breakdown */}
            {categories.length > 0 && (
              <div className={styles.sidebarCard}>
                <h3 className={styles.sidebarTitle}>Categories</h3>
                <div className={styles.categoryBreakdown}>
                  {categories.map(cat => (
                    <div key={cat.id} className={styles.categoryBreakdownItem}>
                      <span className={styles.categoryBreakdownIcon}>{cat.icon}</span>
                      <span className={styles.categoryBreakdownName}>{cat.name}</span>
                      <span className={styles.categoryBreakdownCount}>{categoryCounts[cat.id] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} size="lg">
        <div className={styles.modalHero}>
          <div className={styles.modalHeroLeft}>
            <div className={styles.heroIconBox}>
              <Users size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.modalTitle}>Start a Stream</div>
              <div className={styles.modalSub}>Share with your team</div>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={() => setShowCreateModal(false)}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.form} style={{ padding: '18px 22px' }}>
          {/* Category Select */}
          <div>
            <label className={styles.fieldLabel}>Category</label>
            <select
              className={styles.fieldSelect}
              value={newThread.category_id}
              onChange={(e) => setNewThread({ ...newThread, category_id: e.target.value })}
            >
              <option value="">— Choose a category —</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className={styles.fieldLabel}>Title</label>
            <input
              className={styles.fieldInput}
              placeholder="What's this stream about?"
              value={newThread.title}
              onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
            />
          </div>

          {/* Content */}
          <div>
            <label className={styles.fieldLabel}>Content</label>
            <RichTextEditor
              value={newThread.content}
              onChange={(val) => setNewThread(prev => ({ ...prev, content: val }))}
              placeholder="Share your thoughts…"
              minHeight={160}
            />
          </div>

          {/* Media */}
          <div className={styles.mediaSection}>
            <label className={styles.fieldLabel}>
              Attachments <span className={styles.fieldOptional}>Optional</span> - {createMediaFiles.length}/5
            </label>
            <input type="file" ref={fileInputRef} accept="image/*,video/*" multiple onChange={handleMediaSelect} style={{ display: 'none' }} />
            <input type="file" ref={fileDocInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleMediaSelect} style={{ display: 'none' }} />
            {createMediaFiles.length > 0 && (
              <div className={styles.uploadPreviewGrid}>
                {createMediaFiles.map((mf, i) => (
                  <div key={i} className={styles.uploadPreviewItem}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt={mf.name || 'Preview'} />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} />
                    ) : (
                      <div className={styles.filePreview}><FileText size={18} /><span>{mf.name || 'File'}</span></div>
                    )}
                    <button className={styles.uploadRemoveBtn} onClick={() => handleRemoveMedia(i)} type="button">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {createMediaFiles.length < 5 && (
              <div className={styles.mediaButtons}>
                <button type="button" className={styles.mediaButton} onClick={() => fileInputRef.current?.click()}>
                  <ImageIcon size={18} />
                  <span>Upload media</span>
                </button>
                <button type="button" className={styles.mediaButton} onClick={() => fileDocInputRef.current?.click()}>
                  <FileText size={18} />
                  <span>Upload file</span>
                </button>
              </div>
            )}
          </div>

          {/* Link */}
          <div className={styles.linkSection}>
            <div>
              <label className={styles.fieldLabel}>
                Link <span className={styles.fieldOptional}>Optional</span>
              </label>
              <input
                className={styles.fieldInput}
                placeholder="https://example.com"
                value={newThread.link_url}
                onChange={(e) => setNewThread({ ...newThread, link_url: e.target.value })}
              />
            </div>
            {newThread.link_url && (
              <div>
                <label className={styles.fieldLabel}>Link Title</label>
                <input
                  className={styles.fieldInput}
                  placeholder="Link display text..."
                  value={newThread.link_title}
                  onChange={(e) => setNewThread({ ...newThread, link_title: e.target.value })}
                />
              </div>
            )}
          </div>

          {/* Poll (optional) */}
          {!newPoll ? (
            <button
              type="button"
              className={styles.mediaButton}
              onClick={() => setNewPoll({ question: '', options: ['', ''] })}
              style={{ alignSelf: 'flex-start' }}
            >
              <BarChart3 size={18} />
              <span>Add a poll</span>
            </button>
          ) : (
            <div className={styles.pollEditor}>
              <div className={styles.pollEditorHeader}>
                <div className={styles.pollEditorLabel}>
                  <BarChart3 size={14} /> Poll
                </div>
                <button
                  type="button"
                  className={styles.pollEditorRemove}
                  onClick={() => setNewPoll(null)}
                  title="Remove poll"
                >
                  <X size={14} />
                </button>
              </div>
              <input
                className={styles.fieldInput}
                placeholder="Ask a question…"
                value={newPoll.question}
                onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                maxLength={500}
              />
              <div className={styles.pollOptionsList}>
                {newPoll.options.map((opt, idx) => (
                  <div key={idx} className={styles.pollOptionRow}>
                    <input
                      className={styles.fieldInput}
                      placeholder={`Option ${idx + 1}`}
                      value={opt}
                      onChange={(e) => {
                        const next = [...newPoll.options];
                        next[idx] = e.target.value;
                        setNewPoll({ ...newPoll, options: next });
                      }}
                      maxLength={255}
                    />
                    {newPoll.options.length > 2 && (
                      <button
                        type="button"
                        className={styles.pollOptionRemove}
                        onClick={() => setNewPoll({ ...newPoll, options: newPoll.options.filter((_, i) => i !== idx) })}
                        title="Remove option"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {newPoll.options.length < 6 && (
                <button
                  type="button"
                  className={styles.pollAddOptionBtn}
                  onClick={() => setNewPoll({ ...newPoll, options: [...newPoll.options, ''] })}
                >
                  <Plus size={12} /> Add option
                </button>
              )}
            </div>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerHint}>
            <Eye size={12} /> Visible to all members
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnCancel} onClick={() => setShowCreateModal(false)}>Cancel</button>
            <button
              className={styles.btnPublish}
              onClick={handleCreate}
              disabled={!newThread.category_id || !newThread.title.trim() || !newThread.content.trim() || creating}
            >
              {creating ? 'Publishing...' : 'Publish'}
              {!creating && <SendHorizontal size={13} />}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteId(null); }}
        title="Delete Stream"
      >
        <div className={styles.deleteModal}>
          <p>Are you sure you want to delete this stream?</p>
          <p className={styles.deleteWarning}>
            This action cannot be undone. The thread and all its comments will be permanently removed.
          </p>
          <div className={styles.deleteActions}>
            <Button variant="ghost" onClick={() => { setDeleteModalOpen(false); setDeleteId(null); }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingThread(null); }}
        size="lg"
      >
        <div className={styles.modalHero}>
          <div className={styles.modalHeroLeft}>
            <div className={styles.heroIconBox}>
              <Edit2 size={16} color="#9FE1CB" />
            </div>
            <div>
              <div className={styles.modalTitle}>Edit Stream</div>
              <div className={styles.modalSub}>Update your post details</div>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={() => { setShowEditModal(false); setEditingThread(null); }}>
            <X size={14} />
          </button>
        </div>

        <div className={styles.form} style={{ padding: '18px 22px' }}>
          {/* Category Select */}
          <div>
            <label className={styles.fieldLabel}>Category</label>
            <select
              className={styles.fieldSelect}
              value={editFormData.category_id}
              onChange={(e) => setEditFormData({ ...editFormData, category_id: e.target.value })}
            >
              <option value="">— Choose a category —</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className={styles.fieldLabel}>Title</label>
            <input
              className={styles.fieldInput}
              placeholder="What's this stream about?"
              value={editFormData.title}
              onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
            />
          </div>

          {/* Content */}
          <div>
            <label className={styles.fieldLabel}>Content</label>
            <RichTextEditor
              value={editFormData.content}
              onChange={(val) => setEditFormData(prev => ({ ...prev, content: val }))}
              placeholder="Share your thoughts…"
              minHeight={160}
            />
          </div>

          {/* Media */}
          <div className={styles.mediaSection}>
            <label className={styles.fieldLabel}>
              Attachments <span className={styles.fieldOptional}>Optional</span> - {editMediaFiles.length}/5
            </label>
            <input type="file" ref={editFileInputRef} accept="image/*,video/*" multiple onChange={handleEditMediaSelect} style={{ display: 'none' }} />
            <input type="file" ref={editFileDocInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleEditMediaSelect} style={{ display: 'none' }} />
            {editMediaFiles.length > 0 && (
              <div className={styles.uploadPreviewGrid}>
                {editMediaFiles.map((mf, i) => (
                  <div key={i} className={styles.uploadPreviewItem}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt={mf.name || 'Preview'} />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} />
                    ) : (
                      <div className={styles.filePreview}><FileText size={18} /><span>{mf.name || 'File'}</span></div>
                    )}
                    <button className={styles.uploadRemoveBtn} onClick={() => handleEditRemoveMedia(i)} type="button">
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {editMediaFiles.length < 5 && (
              <div className={styles.mediaButtons}>
                <button type="button" className={styles.mediaButton} onClick={() => editFileInputRef.current?.click()}>
                  <ImageIcon size={18} />
                  <span>Upload media</span>
                </button>
                <button type="button" className={styles.mediaButton} onClick={() => editFileDocInputRef.current?.click()}>
                  <FileText size={18} />
                  <span>Upload file</span>
                </button>
              </div>
            )}
          </div>

          {/* Link */}
          <div className={styles.linkSection}>
            <div>
              <label className={styles.fieldLabel}>
                Link <span className={styles.fieldOptional}>Optional</span>
              </label>
              <input
                className={styles.fieldInput}
                placeholder="https://example.com"
                value={editFormData.link_url}
                onChange={(e) => setEditFormData({ ...editFormData, link_url: e.target.value })}
              />
            </div>
            {editFormData.link_url && (
              <div>
                <label className={styles.fieldLabel}>Link Title</label>
                <input
                  className={styles.fieldInput}
                  placeholder="Link display text..."
                  value={editFormData.link_title}
                  onChange={(e) => setEditFormData({ ...editFormData, link_title: e.target.value })}
                />
              </div>
            )}
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerHint}>
            <Eye size={12} /> Visible to all members
          </div>
          <div className={styles.footerActions}>
            <button className={styles.btnCancel} onClick={() => { setShowEditModal(false); setEditingThread(null); }}>
              Cancel
            </button>
            <button
              className={styles.btnPublish}
              onClick={handleUpdate}
              disabled={!editFormData.category_id || !editFormData.title.trim() || !editFormData.content.trim() || updating}
            >
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxOpen(false)}>
          <button className={styles.lightboxClose} onClick={() => setLightboxOpen(false)}>&#10005;</button>
          {lightboxMedia.length > 1 && lightboxIndex > 0 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i - 1); }}
            >
              &#8249;
            </button>
          )}
          <div className={styles.lightboxContent} onClick={e => e.stopPropagation()}>
            {lightboxMedia[lightboxIndex]?.type === 'image' ? (
              <img src={lightboxMedia[lightboxIndex].url} alt="" />
            ) : (
              <video src={lightboxMedia[lightboxIndex].url} controls autoPlay />
            )}
          </div>
          {lightboxMedia.length > 1 && lightboxIndex < lightboxMedia.length - 1 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxNext}`}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => i + 1); }}
            >
              &#8250;
            </button>
          )}
          {lightboxMedia.length > 1 && (
            <div className={styles.lightboxCounter}>{lightboxIndex + 1} / {lightboxMedia.length}</div>
          )}
        </div>
      )}

      {/* Viewed By Modal */}
      <Modal isOpen={showViewersModal} onClose={() => setShowViewersModal(false)} title="Viewed By">
        <div className={styles.viewersModal}>
          <p className={styles.viewersSubtitle}>
            <Eye size={14} /> {viewersData.length} {viewersData.length === 1 ? 'person' : 'people'} viewed "{viewersTitle}"
          </p>
          {viewersLoading ? (
            <div className={styles.viewersLoading}>Loading...</div>
          ) : viewersData.length === 0 ? (
            <div className={styles.viewersEmpty}>No one has viewed this yet.</div>
          ) : (
            <div className={styles.viewersList}>
              {viewersData.map((viewer: any, idx: number) => (
                <div key={idx} className={styles.viewerRow}>
                  <Avatar src={viewer.avatar_url} name={`${viewer.first_name} ${viewer.last_name}`} size="sm" />
                  <div className={styles.viewerInfo}>
                    <span className={styles.viewerName}>{viewer.first_name} {viewer.last_name}</span>
                  </div>
                  <span className={styles.viewerTime}>
                    {new Date(viewer.viewed_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    {' '}
                    {new Date(viewer.viewed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={showReactionsModal} onClose={() => setShowReactionsModal(false)} title="Reactions">
        <div style={{ minWidth: 280 }}>
          <p style={{ fontSize: 13, color: 'rgba(20,32,29,0.5)', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {reactionsModalData.length} {reactionsModalData.length === 1 ? 'person' : 'people'} reacted
          </p>
          {reactionsModalData.map((reactor: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <Avatar src={reactor.avatar_url} name={`${reactor.first_name} ${reactor.last_name}`} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#14201d' }}>{reactor.first_name} {reactor.last_name}</div>
              </div>
              <span style={{ fontSize: 16 }}>{reactor.emoji}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Streams;
